import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { 
  Plus, 
  Edit2, 
  Save, 
  X, 
  Trash2, 
  Settings, 
  Brain, 
  Target, 
  DollarSign,
  HelpCircle
} from "lucide-react";

interface MaturityNextStep {
  id: string;
  quadrant: string;
  time_horizon: string;
  step_summary: string;
  recommendations: string[];
  created_at: string;
}

interface QuadrantGuidance {
  id: string;
  quadrant: string;
  summary: string;
  symptoms: string;
  risks: string;
  opportunities: string;
  created_at: string;
  updated_at: string;
}

interface RoiRecommendation {
  id: string;
  pillar: string;
  recommendation_type: string;
  recommendation_text: string;
  priority: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

const QUADRANTS = [
  "Foundation Builder",
  "Tactical Automator", 
  "Strategic Transformer",
  "Visionary Orchestrator"
];

const TIME_HORIZONS = [
  "0-3 months",
  "3-6 months", 
  "6-12 months"
];

const ROI_PILLARS = [
  "reconciliation",
  "journals",
  "intercompany",
  "orchestration"
];

const RECOMMENDATION_TYPES = [
  "automation_potential",
  "roi_context",
  "implementation_tips"
];

export default function AdminRecommendationsEditor() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [editingMaturity, setEditingMaturity] = useState<Record<string, boolean>>({});
  const [editingQuadrant, setEditingQuadrant] = useState<Record<string, boolean>>({});
  const [editingRoi, setEditingRoi] = useState<Record<string, boolean>>({});
  const [newMaturityForm, setNewMaturityForm] = useState<Partial<MaturityNextStep>>({});
  const [newRoiForm, setNewRoiForm] = useState<Partial<RoiRecommendation>>({});

  // Fetch maturity next steps
  const { data: maturitySteps = [], isLoading: loadingMaturity } = useQuery({
    queryKey: ["/api/admin/maturity-next-steps"],
    queryFn: () => fetch("/api/admin/maturity-next-steps").then(res => res.json())
  });

  // Fetch quadrant guidance
  const { data: quadrantGuidance = [], isLoading: loadingQuadrant } = useQuery({
    queryKey: ["/api/admin/quadrant-guidance"],
    queryFn: () => fetch("/api/admin/quadrant-guidance").then(res => res.json())
  });

  // Fetch ROI recommendations
  const { data: roiRecommendations = [], isLoading: loadingRoi } = useQuery({
    queryKey: ["/api/admin/roi-recommendations"],
    queryFn: () => fetch("/api/admin/roi-recommendations").then(res => res.json())
  });

  // Maturity mutations
  const createMaturityMutation = useMutation({
    mutationFn: (data: Partial<MaturityNextStep>) =>
      apiRequest("POST", "/api/admin/maturity-next-steps", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/maturity-next-steps"] });
      setNewMaturityForm({});
      toast({ title: "Success", description: "Maturity step created successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const updateMaturityMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<MaturityNextStep> }) =>
      apiRequest("PUT", `/api/admin/maturity-next-steps/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/maturity-next-steps"] });
      setEditingMaturity({});
      toast({ title: "Success", description: "Maturity step updated successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const deleteMaturityMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/admin/maturity-next-steps/${id}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/maturity-next-steps"] });
      toast({ title: "Success", description: "Maturity step deleted successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  // Quadrant mutations
  const updateQuadrantMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<QuadrantGuidance> }) =>
      apiRequest("PUT", `/api/admin/quadrant-guidance/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/quadrant-guidance"] });
      setEditingQuadrant({});
      toast({ title: "Success", description: "Quadrant guidance updated successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  // ROI mutations
  const createRoiMutation = useMutation({
    mutationFn: (data: Partial<RoiRecommendation>) =>
      apiRequest("POST", "/api/admin/roi-recommendations", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/roi-recommendations"] });
      setNewRoiForm({});
      toast({ title: "Success", description: "ROI recommendation created successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const updateRoiMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<RoiRecommendation> }) =>
      apiRequest("PUT", `/api/admin/roi-recommendations/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/roi-recommendations"] });
      setEditingRoi({});
      toast({ title: "Success", description: "ROI recommendation updated successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const deleteRoiMutation = useMutation({
    mutationFn: (id: string) =>
      apiRequest("DELETE", `/api/admin/roi-recommendations/${id}`, {}),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/roi-recommendations"] });
      toast({ title: "Success", description: "ROI recommendation deleted successfully" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    }
  });

  const handleDeleteMaturity = (id: string) => {
    if (window.confirm("Are you sure you want to delete this maturity step? This action cannot be undone.")) {
      deleteMaturityMutation.mutate(id);
    }
  };

  const handleDeleteRoi = (id: string) => {
    if (window.confirm("Are you sure you want to delete this ROI recommendation? This action cannot be undone.")) {
      deleteRoiMutation.mutate(id);
    }
  };

  const handleUpdateMaturity = (id: string, formData: FormData) => {
    const data = {
      quadrant: formData.get("quadrant") as string,
      time_horizon: formData.get("time_horizon") as string,
      step_summary: formData.get("step_summary") as string,
      recommendations: (formData.get("recommendations") as string)?.split('\n').filter(r => r.trim())
    };
    updateMaturityMutation.mutate({ id, data });
  };

  const handleUpdateQuadrant = (id: string, formData: FormData) => {
    const data = {
      summary: formData.get("summary") as string,
      symptoms: formData.get("symptoms") as string,
      risks: formData.get("risks") as string,
      opportunities: formData.get("opportunities") as string
    };
    updateQuadrantMutation.mutate({ id, data });
  };

  const handleUpdateRoi = (id: string, formData: FormData) => {
    const data = {
      pillar: formData.get("pillar") as string,
      recommendation_type: formData.get("recommendation_type") as string,
      recommendation_text: formData.get("recommendation_text") as string,
      priority: parseInt(formData.get("priority") as string),
      is_active: formData.get("is_active") === "true"
    };
    updateRoiMutation.mutate({ id, data });
  };

  return (
    <div className="min-h-screen bg-forestBlack text-white font-['Archivo',sans-serif]">
      <div className="container mx-auto p-6">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-primary mb-2 flex items-center gap-3">
            <Settings className="w-8 h-8" />
            Strategic Recommendations Editor
          </h1>
          <p className="text-gray-300">
            Manage dynamic recommendations shown across all assessment result screens
          </p>
        </div>

        <Tabs defaultValue="maturity" className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-[#0A2A35] border border-primary/20">
            <TabsTrigger 
              value="maturity" 
              className="data-[state=active]:bg-primary data-[state=active]:text-forestBlack flex items-center gap-2"
            >
              <Brain className="w-4 h-4" />
              Maturity Next Steps
            </TabsTrigger>
            <TabsTrigger 
              value="quadrant"
              className="data-[state=active]:bg-primary data-[state=active]:text-forestBlack flex items-center gap-2"
            >
              <Target className="w-4 h-4" />
              Quadrant Guidance
            </TabsTrigger>
            <TabsTrigger 
              value="roi"
              className="data-[state=active]:bg-primary data-[state=active]:text-forestBlack flex items-center gap-2"
            >
              <DollarSign className="w-4 h-4" />
              ROI Recommendations
            </TabsTrigger>
          </TabsList>

          {/* Maturity Next Steps Tab */}
          <TabsContent value="maturity" className="space-y-6">
            <Card className="bg-[#0A2A35] border-primary/20">
              <CardHeader>
                <CardTitle className="text-primary flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Add New Maturity Step
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.currentTarget);
                    const data = {
                      quadrant: formData.get("quadrant") as string,
                      time_horizon: formData.get("time_horizon") as string,
                      step_summary: formData.get("step_summary") as string,
                      recommendations: (formData.get("recommendations") as string)?.split('\n').filter(r => r.trim())
                    };
                    createMaturityMutation.mutate(data);
                    e.currentTarget.reset();
                  }}
                  className="grid grid-cols-2 gap-4"
                >
                  <Select name="quadrant" required>
                    <SelectTrigger className="bg-forestBlack border-primary/30">
                      <SelectValue placeholder="Select Quadrant" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A2A35] border-primary/20">
                      {QUADRANTS.map(q => (
                        <SelectItem key={q} value={q}>{q}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select name="time_horizon" required>
                    <SelectTrigger className="bg-forestBlack border-primary/30">
                      <SelectValue placeholder="Select Time Horizon" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A2A35] border-primary/20">
                      {TIME_HORIZONS.map(t => (
                        <SelectItem key={t} value={t}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Input
                    name="step_summary"
                    placeholder="Step Summary"
                    required
                    className="col-span-2 bg-forestBlack border-primary/30 text-white"
                  />

                  <Textarea
                    name="recommendations"
                    placeholder="Recommendations (one per line)"
                    required
                    className="col-span-2 bg-forestBlack border-primary/30 text-white"
                    rows={3}
                  />

                  <Button 
                    type="submit"
                    className="col-span-2 bg-primary text-forestBlack hover:bg-primary/90"
                    disabled={createMaturityMutation.isPending}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Step
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="space-y-4">
              {loadingMaturity ? (
                <div className="text-center py-8 text-gray-400">Loading maturity steps...</div>
              ) : (
                maturitySteps.map((step: MaturityNextStep) => (
                  <Card key={step.id} className="bg-[#0A2A35] border-primary/20">
                    <CardContent className="p-6">
                      {editingMaturity[step.id] ? (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleUpdateMaturity(step.id, new FormData(e.currentTarget));
                          }}
                          className="space-y-4"
                        >
                          <div className="grid grid-cols-2 gap-4">
                            <Select name="quadrant" defaultValue={step.quadrant}>
                              <SelectTrigger className="bg-forestBlack border-primary/30">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#0A2A35] border-primary/20">
                                {QUADRANTS.map(q => (
                                  <SelectItem key={q} value={q}>{q}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            <Select name="time_horizon" defaultValue={step.time_horizon}>
                              <SelectTrigger className="bg-forestBlack border-primary/30">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#0A2A35] border-primary/20">
                                {TIME_HORIZONS.map(t => (
                                  <SelectItem key={t} value={t}>{t}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <Input
                            name="step_summary"
                            defaultValue={step.step_summary}
                            className="bg-forestBlack border-primary/30 text-white"
                          />

                          <Textarea
                            name="recommendations"
                            defaultValue={step.recommendations?.join('\n') || ''}
                            className="bg-forestBlack border-primary/30 text-white"
                            rows={3}
                          />

                          <div className="flex gap-2">
                            <Button 
                              type="submit"
                              size="sm"
                              className="bg-primary text-forestBlack hover:bg-primary/90"
                            >
                              <Save className="w-4 h-4 mr-2" />
                              Save
                            </Button>
                            <Button 
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingMaturity(prev => ({ ...prev, [step.id]: false }))}
                              className="border-primary/30 text-primary"
                            >
                              <X className="w-4 h-4 mr-2" />
                              Cancel
                            </Button>
                          </div>
                        </form>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Badge className="bg-primary/20 text-primary">
                                {step.quadrant}
                              </Badge>
                              <Badge variant="outline" className="border-primary/30 text-primary">
                                {step.time_horizon}
                              </Badge>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingMaturity(prev => ({ ...prev, [step.id]: true }))}
                                className="border-primary/30 text-primary"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteMaturity(step.id)}
                                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-white font-medium">{step.step_summary}</p>
                          {step.recommendations && step.recommendations.length > 0 && (
                            <ul className="list-disc list-inside text-gray-300 space-y-1">
                              {step.recommendations.map((rec, idx) => (
                                <li key={idx}>{rec}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* Quadrant Guidance Tab */}
          <TabsContent value="quadrant" className="space-y-6">
            <div className="space-y-4">
              {loadingQuadrant ? (
                <div className="text-center py-8 text-gray-400">Loading quadrant guidance...</div>
              ) : (
                quadrantGuidance.map((guidance: QuadrantGuidance) => (
                  <Card key={guidance.id} className="bg-[#0A2A35] border-primary/20">
                    <CardHeader>
                      <CardTitle className="text-primary flex items-center justify-between">
                        <span className="flex items-center gap-2">
                          <Target className="w-5 h-5" />
                          {guidance.quadrant}
                        </span>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setEditingQuadrant(prev => ({ ...prev, [guidance.id]: !prev[guidance.id] }))}
                          className="border-primary/30 text-primary"
                        >
                          <Edit2 className="w-4 h-4" />
                        </Button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {editingQuadrant[guidance.id] ? (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleUpdateQuadrant(guidance.id, new FormData(e.currentTarget));
                          }}
                          className="space-y-4"
                        >
                          <div>
                            <label className="block text-sm font-medium text-primary mb-2">Summary</label>
                            <Textarea
                              name="summary"
                              defaultValue={guidance.summary}
                              className="bg-forestBlack border-primary/30 text-white"
                              rows={2}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-primary mb-2">Symptoms</label>
                            <Textarea
                              name="symptoms"
                              defaultValue={guidance.symptoms}
                              className="bg-forestBlack border-primary/30 text-white"
                              rows={2}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-primary mb-2">Risks</label>
                            <Textarea
                              name="risks"
                              defaultValue={guidance.risks}
                              className="bg-forestBlack border-primary/30 text-white"
                              rows={2}
                            />
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-primary mb-2">Opportunities</label>
                            <Textarea
                              name="opportunities"
                              defaultValue={guidance.opportunities}
                              className="bg-forestBlack border-primary/30 text-white"
                              rows={2}
                            />
                          </div>

                          <div className="flex gap-2">
                            <Button 
                              type="submit"
                              className="bg-primary text-forestBlack hover:bg-primary/90"
                            >
                              <Save className="w-4 h-4 mr-2" />
                              Save Changes
                            </Button>
                            <Button 
                              type="button"
                              variant="outline"
                              onClick={() => setEditingQuadrant(prev => ({ ...prev, [guidance.id]: false }))}
                              className="border-primary/30 text-primary"
                            >
                              <X className="w-4 h-4 mr-2" />
                              Cancel
                            </Button>
                          </div>
                        </form>
                      ) : (
                        <div className="space-y-4">
                          <div>
                            <h4 className="text-sm font-medium text-primary mb-2">Summary</h4>
                            <p className="text-gray-300">{guidance.summary}</p>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-primary mb-2">Symptoms</h4>
                            <p className="text-gray-300">{guidance.symptoms}</p>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-primary mb-2">Risks</h4>
                            <p className="text-gray-300">{guidance.risks}</p>
                          </div>
                          <div>
                            <h4 className="text-sm font-medium text-primary mb-2">Opportunities</h4>
                            <p className="text-gray-300">{guidance.opportunities}</p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>

          {/* ROI Recommendations Tab */}
          <TabsContent value="roi" className="space-y-6">
            <Card className="bg-[#0A2A35] border-primary/20">
              <CardHeader>
                <CardTitle className="text-primary flex items-center gap-2">
                  <Plus className="w-5 h-5" />
                  Add New ROI Recommendation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    const formData = new FormData(e.currentTarget);
                    const data = {
                      pillar: formData.get("pillar") as string,
                      recommendation_type: formData.get("recommendation_type") as string,
                      recommendation_text: formData.get("recommendation_text") as string,
                      priority: parseInt(formData.get("priority") as string),
                      is_active: formData.get("is_active") === "true"
                    };
                    createRoiMutation.mutate(data);
                    e.currentTarget.reset();
                  }}
                  className="grid grid-cols-2 gap-4"
                >
                  <Select name="pillar" required>
                    <SelectTrigger className="bg-forestBlack border-primary/30">
                      <SelectValue placeholder="Select Pillar" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A2A35] border-primary/20">
                      {ROI_PILLARS.map(p => (
                        <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select name="recommendation_type" required>
                    <SelectTrigger className="bg-forestBlack border-primary/30">
                      <SelectValue placeholder="Select Type" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A2A35] border-primary/20">
                      {RECOMMENDATION_TYPES.map(t => (
                        <SelectItem key={t} value={t}>
                          {t.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Textarea
                    name="recommendation_text"
                    placeholder="Recommendation text..."
                    required
                    className="col-span-2 bg-forestBlack border-primary/30 text-white"
                    rows={3}
                  />

                  <Input
                    name="priority"
                    type="number"
                    placeholder="Priority (1-10)"
                    defaultValue="1"
                    min="1"
                    max="10"
                    required
                    className="bg-forestBlack border-primary/30 text-white"
                  />

                  <Select name="is_active" defaultValue="true">
                    <SelectTrigger className="bg-forestBlack border-primary/30">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A2A35] border-primary/20">
                      <SelectItem value="true">Active</SelectItem>
                      <SelectItem value="false">Inactive</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button 
                    type="submit"
                    className="col-span-2 bg-primary text-forestBlack hover:bg-primary/90"
                    disabled={createRoiMutation.isPending}
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Recommendation
                  </Button>
                </form>
              </CardContent>
            </Card>

            <div className="space-y-4">
              {loadingRoi ? (
                <div className="text-center py-8 text-gray-400">Loading ROI recommendations...</div>
              ) : (
                roiRecommendations.map((rec: RoiRecommendation) => (
                  <Card key={rec.id} className="bg-[#0A2A35] border-primary/20">
                    <CardContent className="p-6">
                      {editingRoi[rec.id] ? (
                        <form
                          onSubmit={(e) => {
                            e.preventDefault();
                            handleUpdateRoi(rec.id, new FormData(e.currentTarget));
                          }}
                          className="space-y-4"
                        >
                          <div className="grid grid-cols-2 gap-4">
                            <Select name="pillar" defaultValue={rec.pillar}>
                              <SelectTrigger className="bg-forestBlack border-primary/30">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#0A2A35] border-primary/20">
                                {ROI_PILLARS.map(p => (
                                  <SelectItem key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>

                            <Select name="recommendation_type" defaultValue={rec.recommendation_type}>
                              <SelectTrigger className="bg-forestBlack border-primary/30">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#0A2A35] border-primary/20">
                                {RECOMMENDATION_TYPES.map(t => (
                                  <SelectItem key={t} value={t}>
                                    {t.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <Textarea
                            name="recommendation_text"
                            defaultValue={rec.recommendation_text}
                            className="bg-forestBlack border-primary/30 text-white"
                            rows={3}
                          />

                          <div className="grid grid-cols-2 gap-4">
                            <Input
                              name="priority"
                              type="number"
                              defaultValue={rec.priority}
                              min="1"
                              max="10"
                              className="bg-forestBlack border-primary/30 text-white"
                            />

                            <Select name="is_active" defaultValue={rec.is_active.toString()}>
                              <SelectTrigger className="bg-forestBlack border-primary/30">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="bg-[#0A2A35] border-primary/20">
                                <SelectItem value="true">Active</SelectItem>
                                <SelectItem value="false">Inactive</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="flex gap-2">
                            <Button 
                              type="submit"
                              size="sm"
                              className="bg-primary text-forestBlack hover:bg-primary/90"
                            >
                              <Save className="w-4 h-4 mr-2" />
                              Save
                            </Button>
                            <Button 
                              type="button"
                              size="sm"
                              variant="outline"
                              onClick={() => setEditingRoi(prev => ({ ...prev, [rec.id]: false }))}
                              className="border-primary/30 text-primary"
                            >
                              <X className="w-4 h-4 mr-2" />
                              Cancel
                            </Button>
                          </div>
                        </form>
                      ) : (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Badge className="bg-primary/20 text-primary">
                                {rec.pillar.charAt(0).toUpperCase() + rec.pillar.slice(1)}
                              </Badge>
                              <Badge variant="outline" className="border-primary/30 text-primary">
                                {rec.recommendation_type.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                              </Badge>
                              <Badge 
                                variant={rec.is_active ? "default" : "secondary"}
                                className={rec.is_active ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"}
                              >
                                {rec.is_active ? "Active" : "Inactive"}
                              </Badge>
                              <span className="text-sm text-gray-400">Priority: {rec.priority}</span>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setEditingRoi(prev => ({ ...prev, [rec.id]: true }))}
                                className="border-primary/30 text-primary"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDeleteRoi(rec.id)}
                                className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                          <p className="text-gray-300">{rec.recommendation_text}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}